import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import AssistantPanelDropdownMenu from "components/AssistantPanel/components/DropdownMenu";
import { ChevronDown } from "components/icons/generated";
import Button from "ds/components/Button";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import DropdownMenuSection from "ds/components/DropdownMenu/Section";
import DropdownMenuSectionHeader from "ds/components/DropdownMenu/Section/Header";
import Icon from "ds/components/Icon";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import useSearchAiIntegrations from "shared/AiIntegration/useSearchAiIntegrations";

import useChatStore from "../store/useStore";
import styles from "./styles.module.css";
import { formatModelName, getTooltipContent } from "./helpers";
import { PROVIDER_ICON_MAP, SPACELIFT_DEFAULT_LABEL } from "./constants";

const ChatIntegrationSelect = () => {
  const { integrations } = useSearchAiIntegrations();
  const { integrationId, model, setIntegrationId, setModel } = useChatStore(
    useShallow((state) => ({
      integrationId: state.integrationId,
      model: state.model,
      setIntegrationId: state.setIntegrationId,
      setModel: state.setModel,
    }))
  );
  const integrationsWithModels = useMemo(() => {
    return integrations.filter(
      (integration) => integration.enabled && integration.modelDetails.length > 0
    );
  }, [integrations]);

  const handleSelect = useCallback(
    (id: string, selectedModel: string) => {
      setIntegrationId(id);
      setModel(selectedModel);
    },
    [setIntegrationId, setModel]
  );

  const selectedIntegration = useMemo(
    () => integrations.find((i) => i.id === integrationId),
    [integrations, integrationId]
  );

  const activeLabel = selectedIntegration ? formatModelName(selectedIntegration, model) : undefined;

  const activeIcon = selectedIntegration
    ? PROVIDER_ICON_MAP[selectedIntegration.provider]
    : undefined;

  return (
    <AssistantPanelDropdownMenu
      className={styles.menu}
      triggerComponent={
        <Button endIcon={ChevronDown} variant="ghost" size="small" className={styles.triggerButton}>
          {activeIcon && <Icon src={activeIcon} noShrink className={styles.modelIcon} />}
          <TextEllipsis
            tooltip={getTooltipContent(selectedIntegration, model)}
            forceTooltipActive={!!selectedIntegration}
          >
            {(props) => <span {...props}>{activeLabel}</span>}
          </TextEllipsis>
        </Button>
      }
    >
      {integrationsWithModels.map((integration) => {
        const sectionLabel = integration.isSpaceliftProvided
          ? SPACELIFT_DEFAULT_LABEL
          : integration.name;

        return (
          <DropdownMenuSection key={integration.id}>
            <DropdownMenuSectionHeader>{sectionLabel}</DropdownMenuSectionHeader>
            {integration.modelDetails.map((item) => (
              <DropdownMenuItem
                key={`${integration.id}-${item.id}`}
                onAction={() => handleSelect(integration.id, item.id)}
                active={integration.id === integrationId && item.id === model}
              >
                {PROVIDER_ICON_MAP[integration.provider] && (
                  <Icon src={PROVIDER_ICON_MAP[integration.provider]} noShrink />
                )}
                <Typography variant="p-body3" tag="span" margin="0 0 0 small">
                  {item.name}
                </Typography>
              </DropdownMenuItem>
            ))}
          </DropdownMenuSection>
        );
      })}
    </AssistantPanelDropdownMenu>
  );
};

export default ChatIntegrationSelect;
